import {
 useState, useEffect, useCallback, useRef,
} from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';
import localStorage from 'views_shared/app/javascript/services/localStorage';

/**
 * useMailbox - Custom hook for managing mailbox tab state
 *
 * Handles:
 * - Active tab selection ("focused" | "everything" | "trash")
 * - Persisting preference to localStorage
 * - Fetching unread count for the focused mailbox badge
 * - Deriving mailboxFilter and showTrashed for the conversations API
 *
 * Promoters don't have a "focused" tab — they only see Inbox (=everything) + Trash.
 * For non-media users we default to "everything" and remap any stored "focused"
 * (from a profile-type change or stale state) so they don't land on a hidden tab.
 *
 * @param {string} currentReceiverSlug - The current user's profile slug (used only as the localStorage pref key)
 * @param {number} currentReceiverProfileId - The current user's numeric profile id (stable key for the count query)
 * @param {boolean} isMedia - Whether the current user is media (only media see Focused)
 * @returns {object} Mailbox state and actions
 */
export default function useMailbox(currentReceiverSlug, currentReceiverProfileId, isMedia = true) {
  const defaultMailbox = isMedia ? 'focused' : 'everything';

  const getInitialMailbox = () => {
    const stored = localStorage.inboxMailbox.get(currentReceiverSlug, defaultMailbox);
    if (stored === 'other') return defaultMailbox;
    if (!isMedia && stored === 'focused') return 'everything';
    return stored;
  };

  const [activeMailbox, setActiveMailbox] = useState(getInitialMailbox);
  const [counts, setCounts] = useState({ focused: 0, everything: 0, trash: 0 });

  const selectMailbox = useCallback(
    (mailbox) => {
      setActiveMailbox(mailbox);
      localStorage.inboxMailbox.set(currentReceiverSlug, mailbox);
    },
    [currentReceiverSlug],
  );

  const countsLoadedRef = useRef(false);
  const refreshCounts = useCallback(async () => {
    try {
      const newCounts = await qapi.conversationsMailbox.getUnreadCounts(currentReceiverProfileId, {
        includePitches: isMedia,
      });
      countsLoadedRef.current = true;
      setCounts(newCounts);
    } catch (error) {
      qlog.error('Failed to fetch mailbox counts:', error);
    }
  }, [currentReceiverProfileId, isMedia]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Keep the navbar inbox indicator (its own React root) in step with the tab badges.
  useEffect(() => {
    if (countsLoadedRef.current) qapi.conversationsMailbox.notifyInboxUnreadChanged(counts);
  }, [counts]);

  const decrementFocusedCount = useCallback(() => {
    setCounts((prev) => ({ ...prev, focused: Math.max(0, prev.focused - 1) }));
  }, []);

  const decrementEverythingCount = useCallback(() => {
    setCounts((prev) => ({ ...prev, everything: Math.max(0, prev.everything - 1) }));
  }, []);

  const showTrashed = activeMailbox === 'trash';
  const mailboxFilter = activeMailbox === 'focused' ? 'focused' : null;

  return {
    activeMailbox,
    mailboxFilter,
    showTrashed,
    selectMailbox,
    counts,
    refreshCounts,
    decrementFocusedCount,
    decrementEverythingCount,
  };
}
